// angular
import { Component, effect, inject } from '@angular/core';
import { NgClass } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
// components
import { ButtonComponent } from '@app/core/components/button/button.component';
import { InputComponent } from '@app/core/components/input/input.component';
import { UploadFilesComponent } from '@app/core/components/upload-files/upload-files.component';
// services
import { RecaptchaService } from '@app/shared/services/recaptcha.service';
// types
import { RecaptchaData } from '@app/shared/types/recaptcha-data.type';
// utils
import { DialogRef } from '@app/core/shared/utils/dialog/dialog-ref';
import { DIALOG_DATA } from '@app/core/shared/utils/dialog/dialog-tokens';
// enums & pipes
import { Roles } from '@app/shared/enums/roles.enum';
import { HasAccessPipe } from '@app/shared/pipes/has-access.pipe';
import { environment } from '@environments/environment';

@Component({
  selector: 'pwj-tender-dialog',
  standalone: true,
  imports: [
    InputComponent,
    ReactiveFormsModule,
    FormsModule,
    ButtonComponent,
    TranslateModule,
    UploadFilesComponent,
    NgClass,
  ],
  templateUrl: './tender-dialog.component.html',
  styleUrl: './tender-dialog.component.scss',
})
export class TenderDialogComponent {
  public proposalForm!: FormGroup;
  public formIsSubmitting: boolean = false;
  public isDisabledSubmitBtn: boolean = false;

  public hasAccessToCreateProposal!: boolean;
  public dialogData!: { title: string };

  // dependencies
  private readonly _hasAccessPipe = inject(HasAccessPipe);
  private readonly _fb = inject(FormBuilder);
  private readonly _dialogRef = inject(DialogRef);
  public readonly data: { title: string } = inject(DIALOG_DATA);
  private readonly _recaptchaService = inject(RecaptchaService);

  constructor() {
    this._recaptchaService.recaptchaValidation('proposal_form');
    // set dialog data
    this.dialogData = this.data;
    // initialize form
    this.proposalForm = this._fb.group({
      price: [''],
      eta: [''],
      delivery_city: [''],
      description: [''],
      media_ids: [null],
    });

    // check create proposal roll
    this.hasAccessToCreateProposal = this._hasAccessPipe.transform(
      Roles.CreateProposal
    );

    effect(() => {
      if (this._recaptchaService.recaptchaData() !== null) {
        const captchaData =
          this._recaptchaService.recaptchaData() as RecaptchaData;

        if (captchaData?.score < environment.recaptcha.targetScore) {
          this.isDisabledSubmitBtn = true;
        }
      }
    });

    this.proposalForm.valueChanges.subscribe(_ => {
      if (!this.proposalForm.dirty) {
        this.isDisabledSubmitBtn = true;
      }
    });
  }

  public close() {
    this._dialogRef.close();
  }

  public submitBid() {
    // order form fields validation
    this.proposalForm.markAllAsTouched();
    if (this.proposalForm.invalid || !this.proposalForm.dirty) {
      return;
    }

    // implement proposal data for submitting
    const { price, eta, ...rest } = this.proposalForm.value;

    const proposalData = {
      ...rest,
      price: Number(price),
      eta: Number(eta),
    };
    // close dialog & submit form
    this._dialogRef.close({
      ...proposalData,
      media_ids:
        proposalData.media_ids &&
        proposalData.media_ids?.map((photo: any) => photo._id),
    });
  }
}
